import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Navigate } from "react-router-dom";
import {
  Pill, Target, ScrollText, UserPlus, TestTube, Trash2, FileEdit,
  ClipboardList,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const NUTRIENT_LABEL = {
  sodium_mg: "Sodium", potassium_mg: "Potassium", phosphorus_mg: "Phosphorus",
  protein_g: "Protein", bp_systolic: "BP systolic", bp_diastolic: "BP diastolic",
};
const NUTRIENT_UNIT = {
  sodium_mg: "mg", potassium_mg: "mg", phosphorus_mg: "mg",
  protein_g: "g", bp_systolic: "mmHg", bp_diastolic: "mmHg",
};

/** Convert a raw audit_log row into a human-readable timeline event. */
function humanise(row) {
  const { entity, action, before, after, summary, actor_name, actor_role } = row;
  const parts = [];

  // Icon + color per entity
  const meta = {
    prescribed_targets: { icon: Target, color: "#4ADE80", tag: "Prescribed target" },
    medication:         { icon: Pill,   color: "#F472B6", tag: "Medication" },
    egfr_reading:       { icon: TestTube, color: "#4ADE80", tag: "eGFR reading" },
    acr_reading:        { icon: TestTube, color: "#FBBF24", tag: "ACR reading" },
    patient:            { icon: UserPlus, color: "#4ADE80", tag: "Patient onboarding" },
    patient_profile:    { icon: FileEdit, color: "#818CF8", tag: "Profile" },
    care_transition:    { icon: ScrollText, color: "#60A5FA", tag: "Care Plan" },
  }[entity] || { icon: ScrollText, color: "#A3ACA7", tag: entity };

  // Human title
  let title;
  if (entity === "prescribed_targets") {
    // Build diff of changed keys
    const beforeMap = before || {}, afterMap = after || {};
    const changed = [];
    for (const k of Object.keys(NUTRIENT_LABEL)) {
      const b = beforeMap[k], a = afterMap[k];
      if (b !== a) changed.push({ key: k, before: b, after: a });
    }
    if (changed.length === 0) {
      title = summary || "Prescribed targets updated";
    } else {
      title = (
        <span>
          <span className="font-semibold">{actor_name}</span> updated prescribed targets:
          <ul className="mt-1 ml-3 list-disc space-y-0.5">
            {changed.map((c) => (
              <li key={c.key} className="text-sm">
                {NUTRIENT_LABEL[c.key]}:{" "}
                {c.before != null ? (
                  <span className="text-[#A3ACA7]">{c.before}{NUTRIENT_UNIT[c.key]}</span>
                ) : (
                  <span className="text-[#A3ACA7] italic">guideline</span>
                )}
                <span className="mx-1 text-[#A3ACA7]">→</span>
                {c.after != null ? (
                  <span className="text-[#4ADE80] font-semibold">{c.after}{NUTRIENT_UNIT[c.key]}</span>
                ) : (
                  <span className="text-[#A3ACA7] italic">reset to guideline</span>
                )}
              </li>
            ))}
          </ul>
        </span>
      );
    }
  } else if (entity === "medication") {
    if (action === "refill") {
      title = <span><span className="font-semibold">{actor_name}</span> recorded a refill · <span className="text-[#4ADE80] font-semibold">countdown reset</span></span>;
    } else if (action === "prescribe") {
      const name = after?.name || "medication";
      const dose = after?.dose ? ` ${after.dose}` : "";
      const freq = after?.frequency ? ` (${after.frequency})` : "";
      // Detect override from the audit summary (backend writes "OVERRIDE: <reason>" at the end)
      const overrideMatch = summary && summary.match(/OVERRIDE:\s*(.+)$/);
      title = (
        <span>
          <span className="font-semibold">{actor_name}</span> prescribed <span className="font-semibold">{name}</span>
          <span className="text-white/80">{dose}{freq}</span>
          <span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full text-[#60A5FA] bg-[#60A5FA]/12 border border-[#60A5FA]/30">
            RX
          </span>
          {overrideMatch && (
            <>
              <span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full text-[#F87171] bg-[#F87171]/12 border border-[#F87171]/30">
                OVERRIDE
              </span>
              <div className="text-[11px] text-[#F87171] mt-1 leading-snug">
                Documented reason: <span className="text-white/90 italic">{overrideMatch[1].trim()}</span>
              </div>
            </>
          )}
        </span>
      );
    } else if (action === "create") {
      const name = after?.name || "medication";
      const risk = after?.risk_level;
      title = (
        <span>
          <span className="font-semibold">{actor_name}</span> added <span className="font-semibold">{name}</span>
          {risk && (
            <span
              className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{
                color: risk === "safe" ? "#4ADE80" : risk === "avoid" ? "#F87171" : "#FBBF24",
                background: risk === "safe" ? "rgba(74,222,128,0.12)"
                          : risk === "avoid" ? "rgba(248,113,113,0.12)"
                          : "rgba(251,191,36,0.12)",
              }}
            >
              {risk.toUpperCase()}
            </span>
          )}
        </span>
      );
    } else if (action === "delete") {
      title = <span><span className="font-semibold">{actor_name}</span> removed <span className="font-semibold">{before?.name || "medication"}</span></span>;
    }
  } else if (entity === "egfr_reading") {
    if (action === "create") {
      const src = after?.source === "lab_verified" ? "lab-verified" : "self-reported";
      title = <span><span className="font-semibold">{actor_name}</span> logged eGFR <span className="font-semibold">{after?.egfr}</span> ({src})</span>;
    } else {
      title = <span><span className="font-semibold">{actor_name}</span> deleted eGFR reading ({before?.egfr})</span>;
    }
  } else if (entity === "acr_reading") {
    if (action === "create") {
      const src = after?.source === "lab_verified" ? "lab-verified" : "self-reported";
      title = <span><span className="font-semibold">{actor_name}</span> logged ACR <span className="font-semibold">{after?.acr} mg/g</span> ({src})</span>;
    } else {
      title = <span><span className="font-semibold">{actor_name}</span> deleted ACR reading ({before?.acr})</span>;
    }
  } else if (entity === "patient") {
    title = <span><span className="font-semibold">{actor_name}</span> onboarded patient (baseline eGFR {after?.baseline_egfr}, stage {after?.stage})</span>;
  } else if (entity === "care_transition") {
    // Backend writes summary like: "Care Plan · Transplant Status: X → Y · Modality: A → B"
    // OR for AI approvals: "Approved AI suggestion · Transplant Status → Evaluating · Reasoning: …"
    if (action === "approved_ai_suggestion") {
      const m = (summary || "").match(/Approved AI suggestion · (.+?) → (.+?)(?:\s+·\s+Reasoning:\s*(.+))?$/);
      title = (
        <div>
          <span>
            <span className="font-semibold">{actor_name}</span> approved AI suggestion
            <span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full text-[#60A5FA] bg-[#60A5FA]/12 border border-[#60A5FA]/30">
              AI-ASSIST
            </span>
          </span>
          {m ? (
            <>
              <div className="text-xs text-white/85 mt-2">
                <span className="text-[#A3ACA7]">{m[1]}</span>
                <span className="text-[#60A5FA] mx-1">→</span>
                <span className="text-white font-semibold">{m[2]}</span>
              </div>
              {m[3] && (
                <div className="text-[11px] text-[#A3ACA7] mt-1 italic leading-snug">
                  Reasoning: {m[3]}
                </div>
              )}
            </>
          ) : (
            <div className="text-xs text-white/80 mt-1">{summary}</div>
          )}
        </div>
      );
    } else {
      const rawChanges = (summary || "").replace(/^Care Plan · /, "").split(" · ");
      const changes = rawChanges.filter((c) => c && c.length && c !== "Care Plan touched (no field changed)");
      title = (
        <div>
          <span><span className="font-semibold">{actor_name}</span> updated the ESKD care plan</span>
          {changes.length > 0 && (
            <ul className="mt-2 space-y-1">
              {changes.map((c, i) => {
                const m = c.match(/^(.+?):\s*(.+?)\s*→\s*(.+)$/);
                if (!m) return <li key={i} className="text-xs text-white/80">· {c}</li>;
                return (
                  <li key={i} className="text-xs flex items-center gap-1.5 flex-wrap"
                      data-testid={`care-audit-change-${i}`}>
                    <span className="text-[#A3ACA7]">{m[1]}</span>
                    <span className="line-through text-[#A3ACA7]/70">{m[2]}</span>
                    <span className="text-[#60A5FA]">→</span>
                    <span className="text-white font-semibold">{m[3]}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      );
    }
  } else {
    title = <span>{summary || `${actor_name} ${action} ${entity}`}</span>;
  }

  return { meta, title, action };
}

export default function AuditTrail() {
  const { user, viewingPatient } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Only providers viewing a patient may see this page
  useEffect(() => {
    if (user?.role !== "provider" || !viewingPatient) return;
    (async () => {
      try {
        const res = await api.get("/provider/audit-log", {
          params: { patient_id: viewingPatient.patient_id },
        });
        setRows(res.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, viewingPatient]);

  if (user?.role !== "provider") return <Navigate to="/" replace />;
  if (!viewingPatient) return <Navigate to="/provider" replace />;

  return (
    <div className="max-w-3xl" data-testid="audit-trail-page">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-2xl bg-[#4ADE80]/10 flex items-center justify-center">
          <ClipboardList size={22} className="text-[#4ADE80]" />
        </div>
        <div>
          <div className="ros-label">Audit &amp; history</div>
          <h2 className="font-heading text-2xl mt-1">
            {viewingPatient.name} · chronological events
          </h2>
          <p className="text-xs text-[#A3ACA7] mt-1">
            Every clinical change — prescribed targets, medication updates, lab entries — is recorded here
            with actor and timestamp.
          </p>
        </div>
      </div>

      <div className="mt-8">
        {loading && <div className="text-center text-[#A3ACA7] py-10">Loading history…</div>}
        {!loading && rows.length === 0 && (
          <div className="ros-card text-center py-12">
            <ClipboardList size={36} className="mx-auto text-[#A3ACA7] mb-3" />
            <div className="font-heading text-lg">No events yet</div>
            <div className="text-sm text-[#A3ACA7] mt-1">
              Changes made to this patient will appear here immediately.
            </div>
          </div>
        )}
        <div className="relative">
          {rows.length > 0 && (
            <div className="absolute left-[19px] top-3 bottom-3 w-px bg-[#272E2B]" aria-hidden />
          )}
          <div className="space-y-4">
            {rows.map((row) => {
              const { meta, title } = humanise(row);
              const Icon = meta.icon;
              const timestamp = row.timestamp
                ? formatDistanceToNow(new Date(row.timestamp), { addSuffix: true })
                : "";
              return (
                <div key={row.id} className="relative pl-12" data-testid={`audit-row-${row.id}`}>
                  <div
                    className="absolute left-0 top-0 w-10 h-10 rounded-full flex items-center justify-center border border-[#272E2B] bg-[#080A09]"
                    style={{ boxShadow: `0 0 0 3px #080A09` }}
                  >
                    <Icon size={16} style={{ color: meta.color }} />
                  </div>
                  <div className="ros-card-raised">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border"
                                style={{ color: meta.color, borderColor: `${meta.color}44`, background: `${meta.color}0d` }}>
                            {meta.tag}
                          </span>
                          <span className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">
                            {row.action}
                          </span>
                        </div>
                        <div className="text-sm text-white/90 leading-relaxed">{title}</div>
                        <div className="text-[11px] text-[#A3ACA7] mt-2">
                          {row.actor_name} · {row.actor_role} · {timestamp}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
